import * as React from 'react';
import type {
  DspMutateWriteResult,
  SetEntityStatusToolInput,
  UpdateEntityToolInput
} from 'shared/src/api/ai-media-plan-chat-types';
import { formatVizValue, type CellFormat } from 'shared/src/utils/ai/ai-viz-transformers';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';
import { InfoPanelFrame } from '@/features/ai/shared/info-panel-frame';
import { formatMoney } from 'shared/src/utils/money/money-utils';

const MONEY_FIELDS = new Set(['budget_amount', 'cpm_bid', 'cpc_bid']);

type EditableModeProps = {
  mode: 'editable';
  tool: 'setEntityStatus' | 'updateEntity';
  toolCallId: string;
  input: SetEntityStatusToolInput | UpdateEntityToolInput;
  platformId: number;
  executionLineItemId: string;
  strategyId: string;
  /** Outcome of a resolution made outside this card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  onResolve: (output: DspMutateWriteResult) => void | Promise<void>;
};

type ReadonlyModeProps = {
  mode: 'readonly';
  entityType: string;
  data: Record<string, unknown>;
  schema?: unknown;
  title?: string;
};

export type SchemaEntityCardProps = EditableModeProps | ReadonlyModeProps;

/**
 * Generalized entity card with two modes:
 *
 * - `editable` — a confirmation card for a management mutation on an EXISTING DSP entity
 *   (setEntityStatus / updateEntity), routed through the single dspMutateEntity endpoint. Confirm/cancel
 *   only — the agent proposes exact values; to change them the user cancels and asks the agent to
 *   re-propose. Mirrors the create card's resolve semantics: resolve the tool on success/cancel; on a
 *   failed mutation do NOT resolve, so the card stays available and the agent stays cleanly paused for a
 *   corrected re-proposal.
 * - `readonly` — renders an arbitrary entity record as a labeled field grid, no footer/buttons.
 */
export function SchemaEntityCard(props: SchemaEntityCardProps): JSX.Element {
  if (props.mode === 'readonly') return <ReadonlyEntityCard {...props} />;
  return <EditableEntityCard {...props} />;
}

// Card chrome for the readonly mode (the editable mode renders in the shared CommitCardFrame).
// Agent-emitted (the user asked to look at this entity), so it arrives expanded; the render site
// keys the card on the emitting part so only a NEW lookup re-opens it.
function EntityCardChrome({
  heading,
  children
}: {
  heading: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <InfoPanelFrame title={heading} defaultOpen>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">{children}</dl>
    </InfoPanelFrame>
  );
}

function EditableEntityCard({
  tool,
  toolCallId,
  input,
  platformId,
  executionLineItemId,
  strategyId,
  externalOutcome,
  onResolve
}: EditableModeProps): JSX.Element {
  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  const [successUrl, setSuccessUrl] = React.useState<string | null>(null);

  const mutation = trpc.dspMutateEntity.useMutation();

  const isStatus = tool === 'setEntityStatus';
  const statusInput = input as SetEntityStatusToolInput;
  const fieldsInput = input as UpdateEntityToolInput;

  const fieldChanges: Array<[string, string]> = isStatus
    ? []
    : Object.entries(fieldsInput.fields ?? {})
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, MONEY_FIELDS.has(k) ? formatMoney(Number(v)) : String(v)]);

  const heading = isStatus
    ? `${statusInput.status === 'active' ? 'Activate' : 'Pause'} ${input.entity_type}`
    : `Update ${input.entity_type}`;

  async function handleConfirm() {
    try {
      const res = await mutation.mutateAsync({
        execution_line_item_id: executionLineItemId,
        strategy_id: strategyId,
        platform_id: platformId,
        tool_call_id: toolCallId,
        entity_type: input.entity_type,
        entity_id: input.entity_id,
        account_id: input.account_id,
        operation: isStatus ? 'set_status' : 'update_fields',
        status: isStatus ? statusInput.status : undefined,
        fields: isStatus ? undefined : fieldsInput.fields
      });
      setOutcome({ status: 'applied', summary: appliedSummary(tool, input) });
      if (res.result?.url) setSuccessUrl(res.result.url);
      await onResolve({
        status: res.status,
        entity_type: res.result?.entity_type,
        entity_id: res.result?.entity_id,
        url: res.result?.url,
        summary: res.result?.summary,
        warnings: res.result?.warnings
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to apply the change';
      setOutcome({ status: 'failed', summary: msg });
      showErrorToast(msg);
      // Feed the error back so the agent can explain it and re-propose a corrected change (or relay
      // guidance for partial/system failures). Nothing changed remotely, so a corrected retry is safe.
      await onResolve({ status: 'failed', error: msg });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    await onResolve({ status: 'cancelled' });
  }

  return (
    <>
      <CommitCardFrame
        tier="live"
        title={heading}
        consequence={consequenceLine(tool, input)}
        confirmLabel={confirmLabel(tool, input)}
        isPending={mutation.isPending}
        outcome={outcome}
        externalOutcome={externalOutcome ?? null}
        toolCallId={toolCallId}
        onConfirm={handleConfirm}
        onCancel={handleCancel}>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-500">Entity</dt>
          <dd className="font-medium text-gray-900">
            {input.entity_type} #{input.entity_id}
          </dd>
          {input.account_id ? (
            <>
              <dt className="text-gray-500">Account</dt>
              <dd className="font-medium text-gray-900">{input.account_id}</dd>
            </>
          ) : null}
          {isStatus ? (
            <>
              <dt className="text-gray-500">New status</dt>
              <dd className="font-medium text-gray-900">
                {statusInput.status === 'active' ? 'Active' : 'Paused'}
              </dd>
            </>
          ) : (
            fieldChanges.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-gray-500">{k}</dt>
                <dd className="font-medium text-gray-900">{v}</dd>
              </React.Fragment>
            ))
          )}
        </dl>
      </CommitCardFrame>

      {outcome?.status === 'applied' && successUrl ? (
        <div className="mt-2 px-1">
          <a
            href={successUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-primary hover:underline">
            View in platform &#x2197;
          </a>
        </div>
      ) : null}
    </>
  );
}

function confirmLabel(tool: EditableModeProps['tool'], input: EditableModeProps['input']): string {
  if (tool !== 'setEntityStatus') return 'Apply changes';
  return (input as SetEntityStatusToolInput).status === 'active' ? 'Activate' : 'Pause';
}

function consequenceLine(
  tool: EditableModeProps['tool'],
  input: EditableModeProps['input']
): string {
  if (tool === 'setEntityStatus') {
    return (input as SetEntityStatusToolInput).status === 'active'
      ? `Activates this ${input.entity_type} — it can start spending.`
      : `Pauses this ${input.entity_type} — it stops serving.`;
  }
  return `Edits the live ${input.entity_type} in the platform immediately.`;
}

function appliedSummary(
  tool: EditableModeProps['tool'],
  input: EditableModeProps['input']
): string {
  if (tool === 'setEntityStatus') {
    return (input as SetEntityStatusToolInput).status === 'active'
      ? `Activated ${input.entity_type} #${input.entity_id}`
      : `Paused ${input.entity_type} #${input.entity_id}`;
  }
  return `Updated ${input.entity_type} #${input.entity_id}`;
}

// snake_case / camelCase field key -> Title Case label.
function humanizeFieldKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function pickFieldFormat(value: unknown): CellFormat {
  return typeof value === 'number' ? 'number' : 'string';
}

function ReadonlyEntityCard({ entityType, data, title }: ReadonlyModeProps): JSX.Element {
  const heading = title ?? entityType;
  const fields = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);

  return (
    <EntityCardChrome heading={heading}>
      {fields.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className="text-gray-500">{humanizeFieldKey(key)}</dt>
          <dd className="font-medium text-gray-900">
            {formatVizValue(value, pickFieldFormat(value))}
          </dd>
        </React.Fragment>
      ))}
    </EntityCardChrome>
  );
}
